import {
  Modal, Form, Input, Select, Row, Col, Switch, Typography,
  Alert, Button, Divider, message,
} from 'antd';
import { MailOutlined, InfoCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { schoolApi } from '../../../api/school.api';
import { designationApi } from '../../../api/designation.api';

const StaffForm = ({ open, onClose, onSubmit, initialValues }) => {
  const [form] = Form.useForm();
  const isEdit = !!initialValues;
  const onlyDigits = (maxLength) => (e) => e.target.value.replace(/\D/g, '').slice(0, maxLength);

  const [newDesignName, setNewDesignName] = useState('');
  const newDesignInputRef = useRef(null);
  const queryClient = useQueryClient();

  // ── Schools ────────────────────────────────────────────────────────
  const { data: schoolsData, isFetching: schoolsLoading } = useQuery({
    queryKey: ['schools-all'],
    queryFn:  () => schoolApi.getAll({ limit: 1000 }),
    staleTime: 5 * 60 * 1000,
  });

  const schoolOptions = useMemo(() => {
    const options = (schoolsData?.data || []).map((s) => ({ label: s.name, value: s.id }));
    if (initialValues?.school) {
      const currentId   = initialValues.school?.id   || initialValues.school;
      const currentName = initialValues.school?.name;
      if (currentId && currentName && !options.find((o) => o.value === currentId)) {
        options.unshift({ label: currentName, value: currentId });
      }
    }
    return options;
  }, [schoolsData, initialValues]);

  // ── Designations from DB ──────────────────────────────────────────
  const { data: designationsData, isLoading: designationsLoading } = useQuery({
    queryKey: ['designations'],
    queryFn:  designationApi.getAll,
    staleTime: 5 * 60 * 1000,
  });

  const designationOptions = useMemo(
    () => (designationsData?.data || []).map((d) => ({ label: d.name, value: d.name })),
    [designationsData],
  );

  const addDesignationMutation = useMutation({
    mutationFn: (name) => designationApi.create({ name }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['designations'] });
      form.setFieldValue('designation', res.data.name);
      setNewDesignName('');
      message.success(`"${res.data.name}" added`);
    },
    onError: (err) => message.error(err.message),
  });

  const handleAddDesignation = (e) => {
    e.preventDefault();
    const trimmed = newDesignName.trim();
    if (trimmed.length < 2) return;
    addDesignationMutation.mutate(trimmed);
  };

  // ── Form populate on open ─────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setNewDesignName('');
    if (initialValues) {
      form.setFieldsValue({
        name:        initialValues.name,
        phone:       initialValues.phone,
        designation: initialValues.designation,
        isActive:    initialValues.isActive !== false,
        email:       initialValues.email,
        school:      initialValues.school?.id ?? (typeof initialValues.school === 'string' ? initialValues.school : undefined),
      });
    }
  }, [open, initialValues, form]);

  const phoneRule = {
    pattern: /^0?[1-9][0-9]{9}$/,
    message: 'Enter a valid 10-digit number, or 11 digits with a single leading 0',
  };

  // ── Designation dropdown footer ───────────────────────────────────
  const designationDropdownRender = (menu) => (
    <>
      {menu}
      <Divider style={{ margin: '8px 0' }} />
      <div style={{ display: 'flex', gap: 8, padding: '0 8px 4px' }}>
        <Input
          ref={newDesignInputRef}
          placeholder="New designation name"
          value={newDesignName}
          onChange={(e) => setNewDesignName(e.target.value)}
          onKeyDown={(e) => e.stopPropagation()}
          onPressEnter={handleAddDesignation}
          maxLength={100}
          size="small"
          style={{ flex: 1 }}
        />
        <Button
          type="primary"
          size="small"
          icon={<PlusOutlined />}
          loading={addDesignationMutation.isPending}
          disabled={newDesignName.trim().length < 2}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleAddDesignation}
          style={{ background: 'linear-gradient(135deg, var(--wl-primary-light), var(--wl-primary))', border: 'none' }}
        >
          Add
        </Button>
      </div>
    </>
  );

  return (
    <Modal
      title={isEdit ? 'Edit Staff' : 'Add Staff'}
      open={open}
      onCancel={onClose}
      onOk={async () => { const values = await form.validateFields(); await onSubmit(values); }}
      width={580}
      destroyOnClose
    >
      {!schoolsLoading && schoolOptions.length === 0 && !isEdit && (
        <Alert
          message="No Schools Available"
          description="You must create at least one school before adding staff."
          type="warning"
          showIcon
          style={{ marginBottom: 16, marginTop: 16 }}
        />
      )}

      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label="Staff Name"
              rules={[
                { required: true, message: 'Staff name is required' },
                { max: 100,       message: 'Staff name cannot exceed 100 characters' },
              ]}
            >
              <Input placeholder="Enter name" maxLength={100} showCount />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="phone"
              label="Mobile Number"
              getValueFromEvent={onlyDigits(11)}
              rules={[{ required: true }, phoneRule]}
            >
              <Input placeholder="Enter phone number" inputMode="numeric" pattern="[0-9]*" maxLength={11} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              name="designation"
              label="Designation"
              rules={[{ required: true, message: 'Please select a designation' }]}
            >
              <Select
                placeholder="Select designation"
                showSearch
                optionFilterProp="label"
                options={designationOptions}
                loading={designationsLoading}
                notFoundContent={designationsLoading ? 'Loading…' : 'No designations found'}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                dropdownRender={designationDropdownRender}
                onDropdownVisibleChange={(visible) => {
                  if (visible) setTimeout(() => newDesignInputRef.current?.focus(), 100);
                  else setNewDesignName('');
                }}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name="school" label="School" rules={[{ required: true, message: 'School is required' }]}>
              <Select
                placeholder="Select school"
                showSearch
                optionFilterProp="label"
                options={schoolOptions}
                loading={schoolsLoading}
                disabled={!schoolsLoading && schoolOptions.length === 0}
                notFoundContent={schoolsLoading ? null : 'No matching schools'}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Active / Inactive toggle */}
        <div style={{
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'space-between',
          background:     'var(--wl-primary-pale)',
          borderRadius:   10,
          padding:        '12px 16px',
          marginBottom:   16,
          border:         '1px solid var(--wl-primary-pale)',
        }}>
          <div>
            <Typography.Text strong style={{ color: 'var(--wl-primary)', fontSize: 13 }}>
              Account Status
            </Typography.Text>
            <Typography.Text type="secondary" style={{ display: 'block', fontSize: 11 }}>
              Inactive staff cannot log in to the system
            </Typography.Text>
          </div>
          <Form.Item name="isActive" valuePropName="checked" noStyle initialValue={true}>
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" style={{ minWidth: 88 }} />
          </Form.Item>
        </div>

        {/* Login email */}
        <Form.Item
          name="email"
          label={
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <MailOutlined style={{ color: 'var(--wl-primary)' }} />
              Login Email
            </span>
          }
          rules={[
            { required: true, message: 'Email is required' },
            { type: 'email',  message: 'Enter a valid email address' },
            { pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, message: 'Enter a valid email format (e.g. user@domain.com)' },
          ]}
        >
          <Input
            placeholder="staff@school.com"
            autoComplete="off"
          />
        </Form.Item>

        {/* Info banners */}
        {!isEdit && (
          <Alert
            icon={<InfoCircleOutlined />}
            showIcon
            type="info"
            message="Password setup email will be sent"
            description="A welcome email with a secure link will be sent to the staff's email address so they can create their own password. The link expires in 24 hours."
            style={{ borderRadius: 10, marginTop: 4 }}
          />
        )}
        {isEdit && (
          <Alert
            icon={<InfoCircleOutlined />}
            showIcon
            type="info"
            message="Changing email updates login credentials"
            description={'If you update the email, the staff member must use the new email to log in. To reset their password, ask them to use the "Forgot Password?" link.'}
            style={{ borderRadius: 10, marginTop: 4 }}
          />
        )}
      </Form>
    </Modal>
  );
};

export default StaffForm;
